import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { DataTableCellValue } from 'src/components/data-table/DataTable.types';
import type { ResolvedFilterColumn } from 'src/filters/entity-filter-registry';

export interface PageFilterRegistration {
    /** Stable identity for this table, e.g. its `automationId`. */
    id: string;
    /** Display label for grouping when several tables register on the same page. */
    label: string;
    columns: ResolvedFilterColumn[];
    filters: Record<string, DataTableCellValue[]>;
    onFilterChange: (field: string, values: DataTableCellValue[]) => void;
}

interface PageFilterContextValue {
    /** Every filterable table currently mounted on the page, in registration order. */
    registrations: PageFilterRegistration[];
    register: (r: PageFilterRegistration) => void;
    unregister: (id: string) => void;
}

const PageFilterContext = createContext<PageFilterContextValue | null>(null);

export const PageFilterProvider = ({ children }: { children: ReactNode }) => {
    const [registrationsById, setRegistrationsById] = useState<
        Record<string, PageFilterRegistration>
    >({});

    const register = useCallback((r: PageFilterRegistration) => {
        setRegistrationsById((prev) => ({ ...prev, [r.id]: r }));
    }, []);

    const unregister = useCallback((id: string) => {
        setRegistrationsById((prev) => {
            if (!(id in prev)) return prev;
            const next = { ...prev };
            delete next[id];
            return next;
        });
    }, []);

    const registrations = useMemo(() => Object.values(registrationsById), [registrationsById]);

    const value = useMemo(
        () => ({ registrations, register, unregister }),
        [registrations, register, unregister],
    );

    return <PageFilterContext.Provider value={value}>{children}</PageFilterContext.Provider>;
};

export const usePageFilter = () => {
    const ctx = useContext(PageFilterContext);
    if (!ctx) throw new Error('usePageFilter must be used within PageFilterProvider');
    return ctx;
};
